/**
 * L'ANAGRAFE COMUNE DEI COMPONENTI vista dalla console (capitoli 66 e 162 dell'atlante).
 *
 * Un documento di `catalog_components` è la VERITÀ TECNICA di un modello del mercato
 * (l'inverter, la zavorra, il morsetto): marca, modello, codice del costruttore,
 * specifiche, e le proprietà universali che nessun tenant deve duplicare — logo,
 * texture del materiale, modello 3D, schede. Il COMMERCIALE (prezzo, ricarico, posa)
 * resta sul prodotto del tenant, che qui compare solo come «vestito» collegato.
 *
 * Dall'ondata zero del magazzino (10/09/2026) il componente sa anche COME SI RICONOSCE
 * SULLO SCAFFALE: marchio del registro, codice del costruttore normalizzato, codici a
 * barre, unità con cui si conta, confezioni, e se ogni pezzo porta una matricola.
 */

import type { UnitaMisuraBase } from '@shared/anagrafe/identificativi';

/** Gli stessi tipi media del prodotto v2 (`MediaTipoV2`): un vocabolario solo. */
export type MediaTipoAnagrafe =
  | 'immagine'
  | 'video'
  | 'scheda_tecnica'
  | 'documento'
  | 'brochure'
  | 'modello_3d'
  | 'texture';

export interface MediaAnagrafe {
  tipo: MediaTipoAnagrafe;
  download_url: string;
  storage_path?: string;
  nome?: string;
}

/** Una confezione in cui il componente viaggia: bancale da 31, cartone da 4. */
export interface ConfezioneAnagrafe {
  nome: string;
  pezzi: number;
  /** GTIN della confezione (GS1 assegna un codice per livello di imballo), se noto. */
  gtin?: string;
}

/**
 * Chi cura il componente. `verificato` = la piattaforma; `proposto` = nato da un tenant
 * (dall'ondata 1 del magazzino), visibile solo a lui finché la piattaforma non lo verifica
 * o lo fonde con uno esistente.
 */
export type StatoCuratela = 'verificato' | 'proposto';

/** Quanto il magazzino vuole le matricole (cap. 320): gemello di `RegimeMatricole` in `shared/magazzino/tipi.ts`. */
export const REGIMI_MATRICOLE_CONSOLE = ['obbligatorie', 'facoltative', 'assenti'] as const;
export type RegimeMatricoleConsole = (typeof REGIMI_MATRICOLE_CONSOLE)[number];

export interface ComponenteCanonico {
  id: string;
  categoria_code: string;
  costruttore: string | null;
  modello: string | null;
  codice_costruttore: string | null;
  /** Specifiche tecniche condivise (stessi nomi dei parametri prodotto: peso_kg, …). */
  specs: Record<string, unknown>;
  /** URL del logo del costruttore (base della cascata prodotto→canonico). */
  logo_url?: string;
  media?: MediaAnagrafe[];
  /** I prodotti-listino da cui è nato (fotografia della corsa dell'anagrafe). */
  origine_prodotti: string[];
  attivo: boolean;

  // ---- Riconoscimento in magazzino (capitolo 162) ----
  /** Id del marchio nel registro `marchi`; null = da assegnare o davvero senza marca. */
  marchio_id: string | null;
  /** Vero quando la corsa ha trovato una scrittura che NON è un marchio (fornitore, «generico»). */
  marchio_da_assegnare: boolean;
  marchio_nota?: string;
  /** `codice_costruttore` in un modo solo (maiuscolo, solo lettere e cifre). Derivato, mai battuto. */
  mpn_normalizzato: string | null;
  /** Codici a barre GS1 validati. Attributo, mai chiave. */
  gtin: string[];
  unita_misura_base: UnitaMisuraBase;
  /** `dedotta` dai prodotti d'origine dalla corsa; `confermata` da un umano in console. */
  unita_misura_origine: 'dedotta' | 'confermata';
  confezioni: ConfezioneAnagrafe[];
  /**
   * Eccezione al regime delle matricole della categoria (cap. 320); undefined = vale la categoria. Stessi tre valori
   * di `RegimeMatricole` in `shared/magazzino/tipi.ts`, che la console non importa.
   */
  matricole?: RegimeMatricoleConsole;
  /** Regex di forma della matricola per questo modello (override del marchio). */
  formato_matricola?: string;
  classe_etim?: string;
  stato_curatela: StatoCuratela;
  proposto_da_tenant?: string;
  /** Se è stato fuso in un altro componente (perdente della fusione), l'id del vincitore. */
  fuso_in?: string;
}

/** Il registro dei marchi di piattaforma (`marchi`): un marchio vero = una voce, con gli alias. */
export interface Marchio {
  id: string;
  nome_canonico: string;
  /** Tutte le scritture viste, normalizzate (maiuscolo, solo lettere e cifre). */
  alias: string[];
  logo_url: string | null;
  /** Regex di forma della matricola per il marchio (es. i seriali Jinko sono 24 caratteri). */
  formato_matricola?: string;
  attivo: boolean;
  origine: 'anagrafe' | 'brands' | 'manuale';
}
